import { Button, LinearProgress, Typography } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { MediaDisplay } from '@/features/taxi-home';
import { Stats } from '@/models/ad/types';

type Props = {
  id: string;
  url: string;
  title: string;
  date?: Date;
  stats: Stats;
};

const getProgressColor = (progress: number) => {
  let result;

  if (progress < 40) {
    result = 'success';
  } else if (progress > 40 && progress < 70) {
    result = 'warning';
  } else {
    result = 'error';
  }

  return result;
};
/**
 * @deprecated
 */
export const AdCard: FC<Props> = ({ id, url, title, stats }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/client/details/${id}`);
  };

  return (
    <div className="basis-1/4 p-4">
      <div className="shadow-xs rounded-md overflow-hidden">
        <div onClick={handleClick}>
          <MediaDisplay mediaUrl={url} />
        </div>
        <div className="w-full p-4 flex flex-col justify-between flex-1">
          <div className="flex flex-col grow-1">
            <div className="mb-4">
              <Typography
                variant="body1"
                fontWeight={500}
                onClick={handleClick}
              >
                {title}
              </Typography>
            </div>

            <div className="flex gap-2 ">
              <StatsBlock
                label={t('label.displays')}
                current={stats.displays.current}
                total={stats.displays.total}
              />
              <StatsBlock
                label={t('label.views')}
                current={stats.views.current}
                total={stats.views.total}
              />
            </div>
          </div>

          <div className="flex items-center justify-end h-9 grow-0">
            <Button variant="text" color="secondary" onClick={handleClick}>
              <Typography variant="button" sx={{ textTransform: 'initial' }}>
                {t('my_ads.details_text')}
              </Typography>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatsBlock: FC<{
  label: string;
  current: number;
  total?: number;
}> = ({ label, current, total }) => {
  const progress = total ? getProgressValue(current, total) : null;

  return (
    <div className="flex flex-col basis-1/2">
      <div className="mb-1 text-center">
        <Typography variant="subtitle2">{label}</Typography>
      </div>

      <div className="grow mx-1 justify-center relative">
        <div className="flex grow justify-around items-center bg-slate-100 rounded-xl px-2 mb-2 h-6">
          {total && total > 0 ? (
            <>
              <Typography variant="subtitle2">{current}</Typography>
              <Typography variant="subtitle2">/</Typography>
              <Typography variant="subtitle2">{total}</Typography>
            </>
          ) : (
            <Typography variant="subtitle2">{current}</Typography>
          )}
        </div>

        {total && total > 0 && progress !== null ? (
          <LinearProgress
            variant="determinate"
            value={progress < 100 ? progress : 100}
            color={getProgressColor(progress) as any}
            sx={{
              visibility:
                total && total > 0 && progress !== null ? 'visible' : 'hidden',
              height: 24,
              borderRadius: '12px',
              '& .MuiLinearProgress-bar': {
                borderRadius: '1rem',
              },
            }}
          />
        ) : null}
      </div>
    </div>
  );
};

const getProgressValue = (current: number, total: number) =>
  (current / total) * 100;
